import React, { useState, useEffect, useRef } from 'react';
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Plus, Calendar, Clock, Video, User, MapPin, CalendarIcon, Users, Link2, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from "@/components/ui/label";
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";
import { Instructor, CourseMode } from '@/types';

interface AddSessionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (sessionData: {
    mode: CourseMode;
    startDate: Date;
    startTime: string;
    durationMinutes: number;
    instructorId: string;
    locationName?: string;
    locationAddress?: string;
    teamsMeetingUrl?: string;
    meetingUrlType?: 'hosted' | 'provided';
    customMeetingUrl?: string;
  }) => void;
  courseName: string;
  instructors: Instructor[];
  defaultDurationMinutes?: number;
  defaultMode?: CourseMode;
  courseModes?: CourseMode[];
  onlineSyncDuration?: number;
  inPersonDuration?: number;
}

export function AddSessionDialog({
  open,
  onOpenChange,
  onSubmit,
  courseName,
  instructors,
  defaultDurationMinutes = 60,
  defaultMode = 'onlineSync',
  courseModes = ['onlineSync', 'in-person'],
  onlineSyncDuration,
  inPersonDuration
}: AddSessionDialogProps) {
  // Form states
  const [mode, setMode] = useState<CourseMode>(defaultMode);
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [startTime, setStartTime] = useState('09:00');
  const [durationMinutes, setDurationMinutes] = useState(defaultDurationMinutes);
  const [instructorId, setInstructorId] = useState('');
  const [locationName, setLocationName] = useState('');
  const [locationAddress, setLocationAddress] = useState('');
  const [teamsMeetingUrl, setTeamsMeetingUrl] = useState('');
  const [meetingUrlType, setMeetingUrlType] = useState<'hosted' | 'provided'>('hosted');
  const [customMeetingUrl, setCustomMeetingUrl] = useState('');
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const calendarRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        calendarRef.current &&
        buttonRef.current &&
        !calendarRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsCalendarOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSubmit = () => {
    if (!startDate || !instructorId) return;
    
    onSubmit({
      mode,
      startDate,
      startTime,
      durationMinutes,
      instructorId,
      ...(mode === 'in-person' && {
        locationName,
        locationAddress
      }),
      ...(mode === 'onlineSync' && {
        teamsMeetingUrl: meetingUrlType === 'provided' ? customMeetingUrl : teamsMeetingUrl,
        meetingUrlType,
        customMeetingUrl: meetingUrlType === 'provided' ? customMeetingUrl : undefined
      })
    });

    // Reset form
    setStartDate(undefined);
    setStartTime('09:00');
    setDurationMinutes(defaultDurationMinutes);
    setInstructorId('');
    setLocationName('');
    setLocationAddress('');
    setTeamsMeetingUrl('');
    setMeetingUrlType('hosted');
    setCustomMeetingUrl('');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] p-0">
        {/* Header compacto */}
        <div className="bg-gradient-to-r from-[#3b65f5] to-[#ff4a8f] px-4 py-3 rounded-t-lg">
          <DialogHeader>
            <DialogTitle className="text-white text-lg font-extrabold flex items-center gap-2">
              <Plus className="h-4 w-4" />
              Nova Sessão
            </DialogTitle>
          </DialogHeader>
        </div>

        <div className="p-4 space-y-3">
          {/* Grid compacto com todos os campos */}
          <div className="grid grid-cols-2 gap-3">
            {/* Data */}
            <div className="space-y-1">
              <Label className="text-xs font-extrabold text-gray-700">Data</Label>
              <div className="relative">
                <Button
                  ref={buttonRef}
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-extrabold h-9 text-sm",
                    !startDate && "text-muted-foreground"
                  )}
                  onClick={() => setIsCalendarOpen(!isCalendarOpen)}
                >
                  <CalendarIcon className="mr-2 h-3 w-3" />
                  {startDate ? format(startDate, "dd/MM/yyyy", { locale: ptBR }) : "Selecionar"}
                </Button>
                {isCalendarOpen && (
                  <div ref={calendarRef} className="absolute top-[calc(100%+4px)] left-0 z-50 bg-white border rounded-md shadow-md">
                    <CalendarComponent
                      mode="single"
                      selected={startDate}
                      onSelect={(date: Date | undefined) => {
                        if (date) {
                          setStartDate(date);
                          setIsCalendarOpen(false);
                        }
                      }}
                      disabled={(date) => date < new Date()}
                      initialFocus
                      locale={ptBR}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Horário */}
            <div className="space-y-1">
              <Label className="text-xs font-extrabold text-gray-700">Horário</Label>
              <Input
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="h-9 text-sm"
              />
            </div>

            {/* Duração */}
            <div className="space-y-1">
              <Label className="text-xs font-extrabold text-gray-700">Duração (min)</Label>
              <Input
                type="number"
                min="15"
                max="480"
                step="15"
                value={durationMinutes || ''}
                onChange={(e) => {
                  const duration = e.target.value ? parseInt(e.target.value) : undefined;
                  setDurationMinutes(duration || defaultDurationMinutes);
                }}
                placeholder="60"
                className="h-9 text-sm"
              />
            </div>

            {/* Instrutor */}
            <div className="space-y-1">
              <Label className="text-xs font-extrabold text-gray-700">Instrutor</Label>
              <Select value={instructorId} onValueChange={setInstructorId}>
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue placeholder="Selecionar" />
                </SelectTrigger>
                <SelectContent>
                  {instructors.map((instructor) => (
                    <SelectItem key={instructor.id} value={instructor.id}>
                      {instructor.first_name} {instructor.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {/* Modalidade compacta */}
          <div className="space-y-2">
            <Label className="text-xs font-extrabold text-gray-700">Modalidade</Label>
            <div className="grid grid-cols-2 gap-2">
              {courseModes.includes('onlineSync') && (
                <Button
                  type="button"
                  variant={mode === 'onlineSync' ? 'default' : 'outline'}
                  className={cn(
                    "h-9 text-xs justify-start",
                    mode === 'onlineSync' 
                      ? "bg-[#3b65f5] hover:bg-[#3b65f5]/90" 
                      : ""
                  )}
                  onClick={() => {
                    setMode('onlineSync');
                    if (onlineSyncDuration) {
                      setDurationMinutes(onlineSyncDuration);
                    }
                  }}
                >
                  <Video className="h-3 w-3 mr-1" />
                  Online
                </Button>
              )}
              
              {courseModes.includes('in-person') && (
                <Button
                  type="button"
                  variant={mode === 'in-person' ? 'default' : 'outline'}
                  className={cn(
                    "h-9 text-xs justify-start",
                    mode === 'in-person' 
                      ? "bg-[#c8e600] hover:bg-[#c8e600]/90 text-gray-800" 
                      : ""
                  )}
                  onClick={() => {
                    setMode('in-person');
                    setTeamsMeetingUrl('');
                    if (inPersonDuration) {
                      setDurationMinutes(inPersonDuration);
                    }
                  }}
                >
                  <Users className="h-3 w-3 mr-1" />
                  Presencial
                </Button>
              )}
            </div>
          </div>

          {/* Link da reunião (apenas online) */}
          {mode === 'onlineSync' && (
            <div className="space-y-3">
              <div className="space-y-1">
                <Label className="text-xs font-extrabold text-gray-700">Tipo de Link da Reunião</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    type="button"
                    variant={meetingUrlType === 'hosted' ? 'default' : 'outline'}
                    className={cn(
                      "h-9 text-xs justify-start",
                      meetingUrlType === 'hosted' 
                        ? "bg-[#3b65f5] hover:bg-[#3b65f5]/90" 
                        : ""
                    )}
                    onClick={() => setMeetingUrlType('hosted')}
                  >
                    <Video className="h-3 w-3 mr-1" />
                    Link Gerado
                  </Button>
                  
                  <Button
                    type="button"
                    variant={meetingUrlType === 'provided' ? 'default' : 'outline'}
                    className={cn(
                      "h-9 text-xs justify-start",
                      meetingUrlType === 'provided' 
                        ? "bg-[#3b65f5] hover:bg-[#3b65f5]/90" 
                        : ""
                    )}
                    onClick={() => setMeetingUrlType('provided')}
                  >
                    <Link2 className="h-3 w-3 mr-1" />
                    Link Personalizado
                  </Button>
                </div>
              </div>

              {meetingUrlType === 'provided' && (
                <div className="space-y-1">
                  <Label className="text-xs font-extrabold text-gray-700">Link Personalizado</Label>
                  <div className="relative">
                    <Input
                      value={customMeetingUrl}
                      onChange={(e) => setCustomMeetingUrl(e.target.value)}
                      placeholder="https://teams.microsoft.com/..."
                      className={cn(
                        "h-9 text-sm pr-8",
                        customMeetingUrl && !customMeetingUrl.startsWith('https://') && "border-red-500 focus-visible:ring-red-500"
                      )}
                    />
                    {customMeetingUrl && !customMeetingUrl.startsWith('https://') && (
                      <div className="absolute right-2 top-1/2 -translate-y-1/2 text-red-500">
                        <X className="h-4 w-4" />
                      </div>
                    )}
                  </div>
                  {customMeetingUrl && !customMeetingUrl.startsWith('https://') && (
                    <p className="text-xs text-red-500 mt-1">
                      O link deve começar com "https://"
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Campos de Localização (apenas para modo presencial) */}
          {mode === 'in-person' && (
            <div className="space-y-3 border rounded-lg p-3 bg-gray-50/50">
              <h3 className="text-xs font-extrabold text-gray-700 flex items-center">
                <MapPin className="h-3 w-3 mr-1" />
                Detalhes do Local
              </h3>

              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs font-extrabold text-gray-700">Nome do Local</Label>
                  <Input
                    value={locationName}
                    onChange={(e) => setLocationName(e.target.value)}
                    placeholder="Ex: Laboratório"
                    className="h-9 text-sm"
                  />
                </div>

                <div className="space-y-1">
                  <Label className="text-xs font-extrabold text-gray-700">Endereço</Label>
                  <Input
                    value={locationAddress}
                    onChange={(e) => setLocationAddress(e.target.value)}
                    placeholder="Ex: Rua Exemplo, 123"
                    className="h-9 text-sm"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Footer compacto */}
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button 
              variant="outline" 
              onClick={() => onOpenChange(false)}
              className="h-8 text-sm px-3"
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleSubmit} 
              disabled={!startDate || !instructorId || (mode === 'in-person' && (!locationName || !locationAddress)) || (mode === 'onlineSync' && meetingUrlType === 'provided' && (!customMeetingUrl || !customMeetingUrl.startsWith('https://')))}
              className="h-8 text-sm px-3 bg-[#3b65f5] hover:bg-[#3b65f5]/90"
            >
              <Plus className="h-3 w-3 mr-1" />
              Criar
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}